import {
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Tr,
} from '@chakra-ui/react';
import {
  ColumnDef,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import React, { useMemo } from 'react';

import { date as commonDate } from '@finley-technologies/common';

import {
  InvoiceReportGQLType,
  PaymentInKindServicingFeeDetailsGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { Icon } from 'packages/common-frontend/components/icons';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { Timestamp } from 'packages/common-frontend/components/Timestamp';
import { Typography } from 'packages/common-frontend/components/Typography';
import { dollarNumberWithCommasOrDash } from 'packages/common-frontend/utils';
import { DisbursementNoticeDownloadButton } from 'packages/invoices/ui/disbursements/InvoiceDisbursementDownloadButton';
import { getLoanLabel } from 'packages/invoices/ui/utils';
import { DownloadAllLiveDisbursementNoticesButton } from 'packages/loans/ui/DownloadAllLiveDisbursementNoticesButton';
import {
  TableBETA,
  TableHeaderBETA,
} from '~/packages/common-frontend/tables/TableBETA';

const baseColumnDefs: ColumnDef<
  PaymentInKindServicingFeeDetailsGQLType,
  any
>[] = [
  {
    accessorKey: 'invoicePaymentDate',
    enableSorting: false,
    header: 'Posted Date',
    cell: ({ getValue }) => commonDate.fullDateStrToHumanDateString(getValue()),
  },
  {
    accessorKey: 'capitalProviderName',
    header: 'Lender',
    enableSorting: false,
    cell: ({ getValue }) => getValue() ?? '-',
  },
  {
    accessorKey: 'originatingLoanName',
    enableSorting: false,
    header: 'Capitalized From',
    cell: ({ getValue, row }) => {
      const originatingLoanName = getValue();
      const originatingLoanId: string = row.getValue('originatingLoanId');
      const originatingExternalLoanId: string | null = row.getValue(
        'originatingExternalLoanId',
      );

      const loanLabel = getLoanLabel({
        loanId: originatingLoanId,
        loanName: originatingLoanName,
        externalLoanId: originatingExternalLoanId,
        alwaysIncludeLoanName: true,
      });

      return loanLabel;
    },
    size: 200,
  },
  {
    accessorKey: 'capitalizedLoanName',
    enableSorting: false,
    header: 'Capitalized To',
    cell: ({ getValue, row }) => {
      const capitalizedLoanName = getValue();
      const capitalizedLoanId: string = row.getValue('capitalizedLoanId');
      const capitalizedExternalLoanId: string | null = row.getValue(
        'capitalizedExternalLoanId',
      );

      const loanLabel = getLoanLabel({
        loanId: capitalizedLoanId,
        loanName: capitalizedLoanName,
        externalLoanId: capitalizedExternalLoanId,
        alwaysIncludeLoanName: true,
      });

      return loanLabel;
    },
    size: 200,
  },
  {
    accessorKey: 'invoiceUpdatedAt',
    enableSorting: false,
    header: 'Created At',
    cell: function formatCreatedAt({ getValue }) {
      const value = getValue();
      if (!value) return '-';
      return (
        <Timestamp
          timestamp={value}
          variant="datetime"
          TypographyProps={{ variant: 'base-muted' }}
        />
      );
    },
  },
  {
    accessorKey: 'serviceFeeAmount',
    enableSorting: false,
    header: 'Amount',
    cell: ({ getValue }) => dollarNumberWithCommasOrDash(getValue()),
  },
  {
    accessorKey: 'runningBalance',
    enableSorting: false,
    header: 'Running Balance',
    cell: ({ getValue }) => dollarNumberWithCommasOrDash(getValue()),
  },
  {
    accessorKey: 'report',
    enableSorting: false,
    header: function DownloadAllNoticesCell({ table }) {
      const reports = table
        .getRowModel()
        .rows.map((row) => row.getValue('report'));

      if (!reports.length) {
        return 'Disbursement';
      }

      return (
        <Menu>
          {({ isOpen }) => (
            <>
              <MenuButton
                fontSize="inherit"
                fontWeight="inherit"
                aria-label="Download all disbursements"
                w="100%"
              >
                <HStack justifyContent="space-between">
                  <Typography fontWeight="medium">Disbursement</Typography>
                  <Icon icon="chevron-down" />
                </HStack>
              </MenuButton>
              {/* known MenuList display issue workaround: https://github.com/chakra-ui/chakra-ui/issues/4186 */}
              <MenuList display={isOpen ? 'block' : 'none'}>
                <MenuItem w="100%">
                  <DownloadAllLiveDisbursementNoticesButton
                    invoiceReports={reports as InvoiceReportGQLType[]}
                  />
                </MenuItem>
              </MenuList>
            </>
          )}
        </Menu>
      );
    },
    cell: function DownloadNoticeCell({ getValue }) {
      const report = getValue();
      if (!report) {
        return null;
      }
      return <DisbursementNoticeDownloadButton report={report} />;
    },
  },
  // HIDDEN COLUMNS
  // Used only for metadata on the row
  {
    accessorKey: 'originatingLoanId',
  },
  {
    accessorKey: 'originatingExternalLoanId',
  },
  {
    accessorKey: 'capitalizedLoanId',
  },
  {
    accessorKey: 'capitalizedExternalLoanId',
  },
];

export const CapitalizationServicerFeeTable = ({
  servicingFeeDetails,
  includeFacilityNameColumn,
}: {
  servicingFeeDetails: PaymentInKindServicingFeeDetailsGQLType[];
  includeFacilityNameColumn: boolean;
}) => {
  const columnDefs = useMemo(() => {
    const columns = [...baseColumnDefs];

    if (includeFacilityNameColumn) {
      columns.splice(1, 0, {
        accessorKey: 'creditFacilityName',
        enableSorting: false,
        header: 'Facility',
        cell: ({ getValue }) => getValue() ?? '-',
      });
    }
    return columns;
  }, [includeFacilityNameColumn]);

  const table = useReactTable({
    data: servicingFeeDetails,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: {
        originatingLoanId: false,
        originatingExternalLoanId: false,
        capitalizedLoanId: false,
        capitalizedExternalLoanId: false,
      }, // these are always hidden. the exist to provide metadata on the row
    },
  });

  if (!servicingFeeDetails.length) {
    return (
      <Table borderWidth="1px">
        <TableHeaderBETA table={table} />
        <Tbody>
          <Tr>
            <Td pl="1rem" colSpan={columnDefs.length}>
              No accrued servicer fees found.
            </Td>
          </Tr>
        </Tbody>
      </Table>
    );
  }

  return (
    <Flex direction="column" gap="0.5rem">
      <TableBETA table={table} />
      {servicingFeeDetails.length > 10 && (
        <TanStackPaginationControl table={table} />
      )}
    </Flex>
  );
};
